import { useCallback, useEffect, useRef, useState } from 'react';
import { ConfigProvider, theme, Alert, Button, Card, message, Select, Space, Table, Tag, Typography } from 'antd';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../../firebase';
import { useAuth } from '../../context/DataContext';
import apis from '../../apis';
import { getOrchestraSessionChoice } from '../../utils/orchestraAssignment';

export const orchestraAdmin = async (action, data) => {
    const token = await auth.currentUser?.getIdToken();
    if (!token) throw new Error('Please sign in again.');
    const response = await apis.publicTicket.orchestraAdmin(action, data, token);
    if (response.error) throw new Error(response.error.data?.message || 'Unable to update orchestra attendance.');
    return response.data;
};
const sessionLabel = (session, venues) => `${venues.find(v => v.id === session.venue)?.label || session.venue} | ${session.date} | ${session.time}`;

export default function OrchestraAssignments() {
    const { currentEventId } = useAuth();
    const [groups, setGroups] = useState([]);
    const [event, setEvent] = useState(null);
    const [cursor, setCursor] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(null);
    const [choices, setChoices] = useState({});
    const requestRef = useRef(0);
    const eventIdRef = useRef(currentEventId);
    eventIdRef.current = currentEventId;
    const load = useCallback(async (nextCursor = null) => {
        const request = ++requestRef.current;
        if (!currentEventId || currentEventId !== eventIdRef.current) return;
        setLoading(true);
        try {
            const [result, snapshot] = await Promise.all([
                orchestraAdmin('list', { eventId: currentEventId, cursor: nextCursor }),
                getDoc(doc(db, 'events', currentEventId)),
            ]);
            if (request !== requestRef.current) return;
            setEvent(snapshot.exists() ? snapshot.data() : null);
            setGroups(previous => nextCursor
                ? [...new Map([...previous, ...result.groups].map(group => [group.id, group])).values()]
                : result.groups);
            setCursor(result.nextCursor);
        } catch (error) { if (request === requestRef.current) message.error(error.message); }
        finally { if (request === requestRef.current) setLoading(false); }
    }, [currentEventId]);
    useEffect(() => {
        setGroups([]); setCursor(null); setChoices({}); setEvent(null);
        load();
        return () => { requestRef.current += 1; };
    }, [load]);
    const save = async (group, notifyOnly = false) => {
        setSaving(group.id);
        try {
            const result = await orchestraAdmin(notifyOnly ? 'notify' : 'assign', {
                eventId: group.eventId, registrantId: group.registrantId,
                sessionId: getOrchestraSessionChoice(group, choices),
            });
            if (result.superseded) message.warning('The assignment changed during delivery. Refresh and retry the latest assignment.');
            else if (result.failedNotifications.length) message.warning('Assignment saved; some emails failed. Use Retry email.');
            else message.success('Assignment emails sent.');
            await load();
        } catch (error) { message.error(error.message); }
        finally { setSaving(null); }
    };
    return <ConfigProvider theme={{ algorithm: theme.darkAlgorithm, token: { colorPrimary: '#EBBC64', colorBgContainer: '#1E1E1E', colorText: '#e5cc92' } }}><Card style={{ background: '#121212', color: '#e5cc92', margin: 24 }}>
        <Typography.Title level={3}>Orchestra Assignments</Typography.Title>
        <Alert type="info" showIcon message="Free seating — assign one session to the whole winning performance."
            description="Attendance = paid public competition tickets + paid winner tickets + registered performers once when a winner purchase exists. Planned sessions from Orchestra Attendance prefill new assignments; confirm with Assign & email. Additional purchases need an updated assignment. Assignment is not limited by the performance quota; staff manage orchestra seating capacity." />
        <Button onClick={() => load()} loading={loading} style={{ margin: '16px 0' }}>Refresh</Button>
        <Table rowKey="id" dataSource={groups} loading={loading} pagination={false} scroll={{ x: 1050 }} columns={[
            { title: 'Performance', key: 'name', render: (_, group) => <Space direction="vertical"><strong>{group.name}</strong><span>{group.bookingCount} paid bookings</span>{group.legacyConflict && <Tag color="orange">Legacy allocation requires reconciliation</Tag>}</Space> },
            { title: 'Attendance', key: 'quantity', render: (_, group) => <Space direction="vertical"><strong>{group.publicTicketCount || 0} public + {group.winnerTicketCount ?? group.paidTicketCount} winner + {group.performerCount} performers = {group.quantity}</strong><span>{group.assignment?.quantity || 0} assigned</span>{group.quantity > (group.assignment?.quantity || 0) && <Tag color="gold">{group.quantity - (group.assignment?.quantity || 0)} awaiting assignment</Tag>}</Space> },
            { title: 'Session', key: 'session', render: (_, group) => <Space direction="vertical"><Select style={{ minWidth: 330 }} placeholder="Choose session" value={getOrchestraSessionChoice(group, choices)} disabled={group.legacyConflict || !!saving}
                onChange={value => setChoices(previous => ({ ...previous, [group.id]: value }))}
                options={(event?.orchestraSessions || []).map(session => ({ value: session.id, label: `${sessionLabel(session, event?.venues || [])} · ${Number(session.freeSeatingAssigned || 0) + Number(session.complimentaryClaimed || 0)} assigned · quota ${Number(session.complimentaryQuota || 0)} (informational)` }))} />
                {!group.assignment && choices[group.id] === undefined && group.plannedSession
                    && <Tag color="gold">Planned: {group.plannedSession.groupLabel} · awaiting confirmation</Tag>}
            </Space> },
            { title: 'Actions', key: 'actions', render: (_, group) => <Space direction="vertical">
                <Button onClick={() => save(group)} loading={saving === group.id} disabled={!!saving || group.legacyConflict || !getOrchestraSessionChoice(group, choices)}>Assign & email</Button>
                {group.assignment && <><span>{(group.assignment.notifiedBookingIds || []).length}/{group.assignment.bookingIds.length} emails sent</span><Button disabled={!!saving} onClick={() => save(group, true)}>Retry email</Button></>}
            </Space> },
        ]} />
        {cursor && <Button style={{ marginTop: 16 }} loading={loading} onClick={() => load(cursor)}>Load more</Button>}
    </Card></ConfigProvider>;
}
